/**
 * controlSurfaceRegistry.ts
 *
 * Module-level registry mapping EC instance IDs → their ControlSurface.
 *
 * A ControlSurface is the typed, agent-accessible interface for an EC —
 * a flat map of atom IDs → imperative getValue/setValueValidated closures
 * backed by the EC's live state.
 *
 * The agent tools (ec_surface_list, ec_surface_read, ec_surface_write) query this registry.
 * Surfaces declared with PublicSurfaceAtomDeclaration naturally appear in the registry
 * and become agent-accessible without any extra work.
 */

// TODO(surfaces): Must refactor generated files in this "features" folder
//  -[ ] some legacy names and not used notions ("feature", etc.)
//  -[ ] need to review implementations for code quality, performance etc.,
//  -[ ] need to check jsdoc-s for irrelevant content LLM-s love to add as a
//       response to concrete prompt

import type { LocalStateActionDescriptor } from "@/components/ec/engine/surfaceFramework/definition/actions";
import type { PublicSurfaceAtomDeclaration } from "@/components/ec/engine/surfaceFramework/definition/atomDeclarations";

/** Imperative interface for a single ControlSurface entry. */
export type ControlSurfaceEntry = {
  kind: "state";
  /** Read the current value without React context. */
  getValue(): unknown;
  /**
   * Zod-validate `raw` against the descriptor schema, then write.
   * Throws on validation failure.
   */
  setValueValidated(raw: unknown): void;
  /** The descriptor — source of truth for name, description, and schema. */
  descriptor: PublicSurfaceAtomDeclaration;
  /**
   * Surface this entry belongs to — used for progressive-disclosure grouping
   * in ec_surface_read. Set by useControlSurfaceBindings; optional for
   * manually-created entries.
   */
  surfaceId?: string;
  /** Surface name — shown in layer-1 listing alongside the surfaceId. */
  surfaceName?: string;
  /**
   * Surface summary — one sentence shown in the ec_surface_read summary
   * output so the agent can pick the right surface without reading all details.
   */
  surfaceSummary?: string;
  surfaceDescription?: string;
};

/**
 * Imperative interface for a callable action entry.
 * Registered by useControlSurfaceBindings for non-private surfaces.
 * Queried by ec_surface_read / ec_surface_write — same call path as client code.
 */
export type ActionSurfaceEntry = {
  kind: "action";
  /** Execute the action with the given params. Returns the action's value for
   *  getter-style actions (e.g. getCurrentPipelinePaths); undefined otherwise. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  call(params: any): unknown;
  /** The descriptor — source of truth for id, name, description, paramsSchema. */
  descriptor: LocalStateActionDescriptor<unknown>;
  surfaceId?: string;
  surfaceName?: string;
  surfaceSummary?: string;
  surfaceDescription?: string;
};

/** Type guard — narrows to ActionSurfaceEntry. */
export function isActionEntry(
  entry: ControlSurfaceEntry | ActionSurfaceEntry
): entry is ActionSurfaceEntry {
  return entry.kind === "action";
}

/** Map of atom/action id → imperative entry for one EC instance. */
export type ControlSurface = Record<string, ControlSurfaceEntry | ActionSurfaceEntry>;

/** Keyed by EC instance ID (ecProps.ecId). */
const registry = new Map<string, ControlSurface>();

export function registerControlSurface(ecId: string, surface: ControlSurface): void {
  registry.set(ecId, surface);
}

export function unregisterControlSurface(ecId: string): void {
  registry.delete(ecId);
}

export function getControlSurface(ecId: string): ControlSurface | undefined {
  return registry.get(ecId);
}

export function getAllControlSurfaces(): ReadonlyMap<string, ControlSurface> {
  return registry;
}
